/**
 * @file src/lib/axios.js
 * @description Single axios instance for the entire application.
 *
 * Responsibilities:
 *  - Set baseURL from environment (never hardcoded)
 *  - Attach withCredentials (httpOnly cookie auth)
 *  - 401 → attempt silent token refresh → retry original request
 *  - Refresh failure → clear storage → redirect to landing
 *  - Normalise all error shapes into a consistent ApiError
 *
 * Rules:
 *  - Nothing imports from pages/ or components/ here
 *  - No React
 *  - This file is the ONLY place axios is configured
 *  - All other files import { apiClient } from "lib/axios"
 */

import axios from "axios";
import { API_BASE_URL, API_PREFIX, STORAGE_KEYS } from "config/constants";
import { ROUTES } from "config/routes";

// ---------------------------------------------------------------------------
// ApiError — normalised error shape
// ---------------------------------------------------------------------------

/**
 * Every API error thrown in the application will be an instance of ApiError.
 * This means catch blocks across the codebase have a single predictable shape
 * instead of checking error.response?.data?.message || error.response?.data?.error
 * in every single file.
 *
 * @property {string}  message        - Human-readable error message
 * @property {number}  status         - HTTP status code (0 if network error)
 * @property {string}  code           - Machine-readable code e.g. "VALIDATION_ERROR"
 * @property {Array}   validationErrors - ZATCA / field-level errors if present
 * @property {string|null} uuid       - Invoice UUID if returned by ZATCA endpoints
 * @property {string|null} invoiceNumber
 * @property {number|null} invoiceCounterValue
 */
export class ApiError extends Error {
  constructor({
    message = "An unexpected error occurred",
    status = 0,
    code = "UNKNOWN_ERROR",
    validationErrors = [],
    uuid = null,
    invoiceNumber = null,
    invoiceCounterValue = null,
  } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.validationErrors = validationErrors;
    this.uuid = uuid;
    this.invoiceNumber = invoiceNumber;
    this.invoiceCounterValue = invoiceCounterValue;
  }
}

// ---------------------------------------------------------------------------
// Error normaliser
// ---------------------------------------------------------------------------

/**
 * Converts any axios error into a consistent ApiError.
 * Handles the three ZATCA error shapes found across invoiceApis.js:
 *  1. { details: { errors: string[] }, uuid, invoiceNumber, invoiceCounter }
 *  2. { message | error, uuid, invoiceNumber, invoiceCounter }
 *  3. Network / timeout errors (no response)
 */
const normaliseError = (error) => {
  // Network error — no response from server
  if (!error.response) {
    return new ApiError({
      message: "Network error — please check your connection",
      status: 0,
      code: "NETWORK_ERROR",
    });
  }

  const { data, status } = error.response;

  // ZATCA validation error shape — details.errors is an array of strings
  if (data?.details?.errors && Array.isArray(data.details.errors)) {
    return new ApiError({
      message: data.details.errors.join(", "),
      status,
      code: "ZATCA_VALIDATION_ERROR",
      validationErrors: data.details.errors,
      uuid: data.uuid ?? null,
      invoiceNumber: data.invoiceNumber ?? null,
      invoiceCounterValue: data.invoiceCounter ?? null,
    });
  }

  // Standard error shape
  const message =
    data?.message ||
    data?.error ||
    error.message ||
    "An unexpected error occurred";

  return new ApiError({
    message,
    status,
    code: data?.code ?? "SERVER_ERROR",
    uuid: data?.uuid ?? null,
    invoiceNumber: data?.invoiceNumber ?? null,
    invoiceCounterValue: data?.invoiceCounter ?? null,
  });
};

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

export const apiClient = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  withCredentials: true, // httpOnly cookie auth
  headers: {
    "Content-Type": "application/json",
  },
});

// ---------------------------------------------------------------------------
// Request interceptor
// ---------------------------------------------------------------------------
// Currently a passthrough — placeholder for adding request-level logic
// e.g. request IDs, logging, adding headers from store

apiClient.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(normaliseError(error)),
);

// ---------------------------------------------------------------------------
// Response interceptor — 401 silent refresh + error normalisation
// ---------------------------------------------------------------------------

/**
 * Flag to prevent multiple simultaneous refresh attempts.
 * If two requests fail with 401 at the same time, only one refresh
 * call is made and both requests are retried after it resolves.
 */
let isRefreshing = false;

/**
 * Queue of callbacks waiting for the refresh to complete.
 * Each entry is { resolve, reject } for a pending request.
 */
let refreshQueue = [];

const processRefreshQueue = (error) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve();
    }
  });
  refreshQueue = [];
};

apiClient.interceptors.response.use(
  // Success — pass through
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    // ── Skip refresh for auth endpoints to avoid infinite loops ────────────
    const isAuthEndpoint =
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/signup") ||
      originalRequest.url?.includes("/auth/refresh") ||
      originalRequest.url?.includes("/auth/verify-otp");

    if (isAuthEndpoint) {
      return Promise.reject(normaliseError(error));
    }

    // ── 401 → attempt silent token refresh ────────────────────────────────
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        })
          .then(() => apiClient(originalRequest))
          .catch((err) => Promise.reject(normaliseError(err)));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.post(
          `${API_BASE_URL}${API_PREFIX}/auth/refresh`,
          {},
          { withCredentials: true },
        );

        // Refresh succeeded — retry all queued requests
        processRefreshQueue(null);
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed — clear storage and redirect to landing
        processRefreshQueue(refreshError);
        localStorage.removeItem(STORAGE_KEYS.COMPANY_ONBOARDING);

        if (
          typeof window !== "undefined" &&
          window.location.pathname !== ROUTES.LANDING
        ) {
          window.location.href = ROUTES.LANDING;
        }

        return Promise.reject(normaliseError(refreshError));
      } finally {
        isRefreshing = false;
      }
    }

    // ── All other errors — normalise and reject ───────────────────────────
    return Promise.reject(normaliseError(error));
  },
);

export default apiClient;
